import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment, useCallback, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PortalIdParser from 'PortalIdParser';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import * as PlanType from 'ecosystem-marketplace-core/constants/pricings/PlanType';
import * as ScreenSizes from 'ecosystem-marketplace-core/constants/layout/ScreenSizes';
import { useAssetInstallability } from 'ecosystem-marketplace-core/assets/hooks/useAssetInstallability';
import { useAssetInstallabilityMessage } from 'ecosystem-marketplace-core/assets/hooks/useAssetInstallabilityMessage';
import { useResponsiveProps } from 'ecosystem-marketplace-core/hooks/useResponsiveProps'; // @ts-expect-error module is untyped

// @ts-expect-error module is untyped
import { selectPortalScopes, selectUserScopes } from '../../selectors/auth';
import { getAppContextCoreType, getIsPublicApp } from '../../selectors/app';
import { getThemeEventSelectorId } from '../../utils/hsAnalytics';
import { getDownloadedAssetsUpdateUrl } from 'ecosystem-marketplace-core/assets/utils/urls'; // @ts-expect-error module is untyped

import CurrencyText from './CurrencyText';
import ThemeDetailsUsageTrackerContext from '../../context/ThemeDetailsUsageTrackerContext'; // @ts-expect-error module is untyped

import ThemeOrderSummaryModal from './ThemeOrderSummaryModal';
import ThemeSignInButton from './ThemeSignInButton';

const ThemeDownloadButton = (_ref) => {
  let {
    disabled,
    section,
    themeDetails,
    themeScopes
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["disabled", "section", "themeDetails", "themeScopes"]);

  const {
    offering: {
      slug
    },
    offeringId,
    pricing
  } = themeDetails;
  const {
    trackInteraction
  } = useContext(ThemeDetailsUsageTrackerContext);
  const appContext = useSelector(getAppContextCoreType);
  const portalScopes = useSelector(selectPortalScopes);
  const userScopes = useSelector(selectUserScopes);
  const assetInstallabilityResult = useAssetInstallability({
    appContext,
    offeringId,
    portalScopes,
    userScopes
  });
  const isPublicApp = useSelector(getIsPublicApp);
  const {
    installability
  } = assetInstallabilityResult;
  const {
    installed,
    canUpdate
  } = installability || {};
  const isUpdateAvailable = installed && canUpdate;
  const isFree = pricing.planType === PlanType.FREE;
  const {
    hasAccess,
    message
  } = useAssetInstallabilityMessage(Object.assign({}, assetInstallabilityResult, {
    offeringId,
    offeringSlug: slug,
    portalId: PortalIdParser.get()
  }));
  const isMissingThemeScopes = themeScopes == null && !isFree;
  const isInstallable = hasAccess && !isMissingThemeScopes;
  const {
    isMobileView
  } = useResponsiveProps({
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.MD]: false
    }
  });
  const [orderSummaryOpen, setOrderSummaryOpen] = useState(false);
  const handleClick = useCallback(() => {
    setOrderSummaryOpen(true);
    trackInteraction('click install button', {
      section
    });
  }, [section, trackInteraction]);
  const computedButtonProps = useMemo(() => {
    if (isUpdateAvailable) {
      return {
        href: getDownloadedAssetsUpdateUrl(offeringId)
      };
    }

    return {};
  }, [isUpdateAvailable, offeringId]);
  const renderedButtonText = useMemo(() => {
    if (isUpdateAvailable) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.themes.details.downloadButton.updateAvailable"
      });
    }

    if (installed) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.themes.details.downloadButton.installed"
      });
    }

    if (isFree) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.themes.details.downloadButton.free"
      });
    }

    return isMobileView ? /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: "marketplace-assets-ui.themes.details.downloadButton.paidWithPrice",
      options: {
        price: /*#__PURE__*/_jsx(CurrencyText, {
          cents: pricing.cents
        })
      }
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "marketplace-assets-ui.themes.details.downloadButton.paid"
    });
  }, [isUpdateAvailable, installed, isFree, isMobileView, pricing]);
  return isPublicApp ? /*#__PURE__*/_jsx(ThemeSignInButton, Object.assign({}, rest, {
    isFree: isFree,
    section: section,
    slug: slug
  })) : /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(UITooltip, {
      disabled: !message,
      title: message,
      children: /*#__PURE__*/_jsx(UIButton, Object.assign({}, rest, {}, computedButtonProps, {
        "data-hs-analytics-id": getThemeEventSelectorId('DOWNLOAD'),
        disabled: disabled || !isInstallable,
        onClick: handleClick,
        use: "primary",
        children: renderedButtonText
      }))
    }), orderSummaryOpen && /*#__PURE__*/_jsx(ThemeOrderSummaryModal, {
      onClose: () => setOrderSummaryOpen(false),
      themeDetails: themeDetails
    })]
  });
};

export default ThemeDownloadButton;